import React, { useState, useEffect } from 'react';
import '../css/BlogReviewModal.css';
import DOMPurify from 'dompurify';
import { doc, getDoc } from 'firebase/firestore';
import { firestore } from '../firebase';

const MAX_REVIEW_LENGTH = 2000;

const BlogReviewModal = ({ book, user, onSubmit, onClose }) => {
  const [review, setReview] = useState('');
  const [rating, setRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [username, setUsername] = useState('');

  useEffect(() => {
    const fetchUsername = async () => {
      if (!user?.uid) {
        setUsername('');
        return;
      }
      
      try {
        const userDoc = await getDoc(doc(firestore, "users", user.uid));
        if (userDoc.exists()) {
          const fetchedUsername = userDoc.data().username;
          // Only update if the username has changed
          if (fetchedUsername !== username) {
            setUsername(fetchedUsername);
          }
        }
      } catch (error) {
        console.error("Error fetching username:", error);
      }
    };

    fetchUsername();
  }, [user?.uid, username]); // Added 'username' to the dependency array

  const getUsername = () => {
    if (username) return username;
    if (user?.email) return user.email.split('@')[0];
    if (user?.displayName) return user.displayName;
    return 'Anonymous';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!review.trim() || rating === 0) return;
    setSubmitting(true);
    await onSubmit({
      bookId: book.id,
      bookTitle: book.title,
      coverUrl: book.coverUrl,
      postText: DOMPurify.sanitize(review.trim()),
      rating,
      userId: user?.uid,
      username: getUsername(),
      timestamp: new Date()
    });
    setSubmitting(false);
  };

  return (
    <div className="blog-review-modal-overlay">
      <div className="blog-review-modal">
        <button className="close-btn" onClick={onClose}>&times;</button>
        <div className="modal-header">
          <img src={book.coverUrl} alt={book.title} className="modal-book-cover" />
          <div className="modal-book-title">{book.title}</div>
        </div>
        <form onSubmit={handleSubmit}>
          <label htmlFor="review-text">Your Review</label>
          <textarea
            id="review-text"
            value={review}
            onChange={e => setReview(e.target.value)}
            placeholder="Share your thoughts about this book..."
            rows={4}
            maxLength={MAX_REVIEW_LENGTH}
            required
          />
          <div style={{ textAlign: 'right', fontSize: '0.8rem', color: '#666', marginTop: '0.5rem', marginBottom: '1rem' }}>
            {review.length}/{MAX_REVIEW_LENGTH} characters
          </div>
          <div className="modal-rating">
            <span>Rating: </span>
            {[1,2,3,4,5].map(star => (
              <span
                key={star}
                className={`star ${star <= rating ? 'filled' : ''}`}
                onClick={() => setRating(star)}
                role="button"
                tabIndex={0}
                aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
              >
                ★
              </span>
            ))}
          </div>
          <div className="modal-actions">
            <button type="submit" disabled={submitting || !review.trim() || rating === 0}>
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
            <button type="button" className="skip-btn" onClick={onClose} disabled={submitting}>
              Skip
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlogReviewModal; 